/**
 * Recomendação de compra on-premise.
 *
 * É o produto final do projeto: a tela que justifica dezenas de milhares de
 * dólares. Se a apresentação não deixar evidente **o que foi medido e o que foi
 * inferido**, a decisão será tomada com confiança maior do que os dados
 * sustentam — e esse é o modo de falha mais caro possível aqui, porque não
 * aparece em teste automatizado, só quando alguém lê a tela e conclui.
 */

import { useMemo, useState } from "react";
import { Badge, Button, Input } from "@cloudflare/kumo";
import { DataTable, type Column } from "../components/DataTable.js";
import { Notice } from "../components/Notice.js";
import { Page } from "../components/Page.js";
import { Panel } from "../components/Panel.js";
import { ProvenanceValue } from "../components/ProvenanceValue.js";
import { Stat } from "../components/Stat.js";
import { snapshot } from "../data/source.js";
import { TARGET_TOKENS_PER_SEC } from "../fixtures/scale.js";
import type { AdvisorCandidate } from "../fixtures/index.js";
import { clampNumber, formatNumber, parseNumberInput } from "../lib/format.js";

/** Custo por hora da instância de nuvem equivalente ao candidato principal. */
const CLOUD_USD_HOUR = 30.13;

/** Câmbio usado para apresentar o custo elétrico, configurável na tela. */
const DEFAULT_FX = 5.4;

/** Tarifa industrial padrão, em reais por quilowatt-hora. */
const DEFAULT_TARIFF = 0.75;

/** Horas de uso por ano em regime de horário comercial. */
const BUSINESS_HOURS_YEAR = 8 * 250;

/** Horas de uso por ano em regime contínuo. */
const CONTINUOUS_HOURS_YEAR = 8_760;

/** Menor tarifa aceita: abaixo disso o custo elétrico some e o retorno fica irreal. */
const MIN_TARIFF = 0.1;

/** Menor câmbio aceito; o real nunca valeu mais que o dólar. */
const MIN_FX = 1;

/**
 * Fator de refrigeração sobre a potência dos aceleradores.
 *
 * Cada watt de GPU exige cerca de 0,4 W a mais de ar-condicionado e perdas; é o PUE
 * típico de sala técnica pequena, e a tela o declara no texto.
 */
const COOLING_FACTOR = 1.4;

/** Ranking de candidatos, com custo total e tempo de retorno. */
export function Advisor(): React.ReactElement {
  const [tariffRaw, setTariffRaw] = useState(String(DEFAULT_TARIFF));
  const [fxRaw, setFxRaw] = useState(String(DEFAULT_FX));
  const [hoursRaw, setHoursRaw] = useState(String(BUSINESS_HOURS_YEAR));

  // O texto do campo fica como foi digitado; o cálculo usa o número já saneado.
  const tariff = clampNumber(parseNumberInput(tariffRaw, DEFAULT_TARIFF), MIN_TARIFF, Number.POSITIVE_INFINITY);
  const fx = clampNumber(parseNumberInput(fxRaw, DEFAULT_FX), MIN_FX, Number.POSITIVE_INFINITY);
  const hoursYear = clampNumber(parseNumberInput(hoursRaw, BUSINESS_HOURS_YEAR), 1, CONTINUOUS_HOURS_YEAR);

  const candidates = snapshot.advisor;
  const emulable = candidates.filter((c) => c.notEmulableReason === null);
  const notEmulable = candidates.filter((c) => c.notEmulableReason !== null);
  const cloudYear = CLOUD_USD_HOUR * hoursYear;

  const regimeLabel =
    hoursYear === BUSINESS_HOURS_YEAR ? "8 h/dia × 250 dias" : hoursYear === CONTINUOUS_HOURS_YEAR ? "contínuo" : "personalizado";

  /** Colunas do ranking; dependem dos parâmetros porque energia e retorno são recalculados. */
  const columns = useMemo<readonly Column<AdvisorCandidate>[]>(() => {
    /** Custo elétrico anual em dólares, com os parâmetros correntes da tela. */
    const opexFor = (candidate: AdvisorCandidate): number =>
      ((candidate.powerW / 1_000) * COOLING_FACTOR * hoursYear * tariff) / fx;

    /** Meses até o custo próprio empatar com o equivalente em nuvem. */
    const paybackFor = (candidate: AdvisorCandidate): number => {
      const saved = CLOUD_USD_HOUR * hoursYear - opexFor(candidate);
      return saved <= 0 ? Number.POSITIVE_INFINITY : (candidate.capexUsd / saved) * 12;
    };

    return [
      {
        key: "label",
        title: "configuração",
        render: (row) => (
          <div className="flex min-w-48 flex-col gap-1">
            <span className="text-sm font-semibold text-kumo-strong">{row.label}</span>
            <span className="text-xs text-kumo-subtle">
              {row.totalVramGb} GB · {formatNumber(row.totalBandwidthTbs, 2)} TB/s · {row.powerW} W
            </span>
            {!row.meetsTarget && (
              <span>
                <Badge variant="error">não atende ao alvo</Badge>
              </span>
            )}
          </div>
        ),
      },
      {
        key: "perf",
        title: "desempenho previsto",
        render: (row) => (
          <div className="min-w-56">
            <ProvenanceValue value={row.predictedTokensPerSec} suffix="tok/s" />
          </div>
        ),
      },
      {
        key: "vram",
        title: "folga de memória",
        render: (row) => (
          <div className="min-w-48">
            <ProvenanceValue value={row.vramHeadroomPct} suffix="%" />
          </div>
        ),
      },
      {
        key: "agg",
        title: "vazão agregada",
        render: (row) => (
          <div className="min-w-48">
            <ProvenanceValue value={row.predictedAggregateTokensPerSec} suffix="tok/s" />
          </div>
        ),
      },
      {
        // A métrica de comparação universal: normaliza portes diferentes
        // e é a única que permite comparar comprar contra alugar.
        key: "costPerM",
        title: "US$ por 1M tokens",
        sortValue: (row) => row.costPerMillionTokensUsd.value,
        render: (row) => (
          <div className="min-w-48">
            <ProvenanceValue value={row.costPerMillionTokensUsd} precision={3} />
          </div>
        ),
      },
      {
        key: "perWatt",
        title: "tokens por watt",
        sortValue: (row) => row.tokensPerWatt.value,
        render: (row) => (
          <div className="min-w-48">
            <ProvenanceValue value={row.tokensPerWatt} suffix="tok/s/W" precision={2} />
          </div>
        ),
      },
      {
        key: "sessions",
        title: "sessões no alvo",
        render: (row) => (
          <div className="min-w-44">
            <ProvenanceValue value={row.concurrentSessionsAtTarget} suffix="sessões" />
          </div>
        ),
      },
      {
        key: "capex",
        title: "custo inicial",
        align: "right",
        sortValue: (row) => row.capexUsd,
        render: (row) => (
          <span className="whitespace-nowrap font-semibold text-kumo-strong">US$ {formatNumber(row.capexUsd)}</span>
        ),
      },
      {
        key: "opex",
        title: "energia por ano",
        align: "right",
        render: (row) => (
          <div className="flex flex-col whitespace-nowrap">
            <span>US$ {formatNumber(Math.round(opexFor(row)))}</span>
            <span className="text-xs text-kumo-subtle">R$ {formatNumber(Math.round(opexFor(row) * fx))}</span>
          </div>
        ),
      },
      {
        key: "payback",
        title: "retorno",
        align: "right",
        render: (row) => {
          const months = paybackFor(row);
          return (
            <div className="flex flex-col whitespace-nowrap">
              <span className={`font-semibold ${Number.isFinite(months) ? "text-kumo-success" : "text-kumo-danger"}`}>
                {Number.isFinite(months) ? `${formatNumber(months, 1)} meses` : "não compensa"}
              </span>
              <span className="text-xs text-kumo-subtle">com os parâmetros acima</span>
            </div>
          );
        },
      },
    ];
  }, [tariff, fx, hoursYear]);

  return (
    <Page>
      <Notice
        tone="warning"
        title="Nem tudo nesta tela foi medido"
        description={
          <span>
            Valores marcados <Badge variant="info">medido</Badge> vêm da bancada. Valores marcados{" "}
            <Badge variant="warning">extrapolado</Badge> derivam da eficiência de banda observada em outro
            hardware, e cada um carrega a limitação correspondente — colada no número, e não em nota de rodapé.
          </span>
        }
      />

      <Panel
        title="Parâmetros do cálculo"
        description={`Todos configuráveis, com o valor padrão à vista. O fator de refrigeração assumido é ${formatNumber(COOLING_FACTOR, 1)}.`}
        bodyClassName="grid gap-5 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1.4fr]"
      >
        <Input
          label="tarifa de energia (R$/kWh)"
          inputMode="decimal"
          value={tariffRaw}
          onChange={(event) => setTariffRaw(event.target.value)}
          description={`padrão ${formatNumber(DEFAULT_TARIFF, 2)} · mínimo ${formatNumber(MIN_TARIFF, 2)}`}
        />
        <Input
          label="câmbio (R$/US$)"
          inputMode="decimal"
          value={fxRaw}
          onChange={(event) => setFxRaw(event.target.value)}
          description={`padrão ${formatNumber(DEFAULT_FX, 2)}`}
        />
        <div className="flex flex-col gap-2 md:col-span-2 xl:col-span-1">
          <Input
            label="regime de uso (horas por ano)"
            inputMode="numeric"
            value={hoursRaw}
            onChange={(event) => setHoursRaw(event.target.value)}
            description={`${regimeLabel} · entre 1 e ${formatNumber(CONTINUOUS_HOURS_YEAR)}`}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={hoursYear === BUSINESS_HOURS_YEAR ? "primary" : "secondary"}
              onClick={() => setHoursRaw(String(BUSINESS_HOURS_YEAR))}
            >
              comercial · {formatNumber(BUSINESS_HOURS_YEAR)} h
            </Button>
            <Button
              size="sm"
              variant={hoursYear === CONTINUOUS_HOURS_YEAR ? "primary" : "secondary"}
              onClick={() => setHoursRaw(String(CONTINUOUS_HOURS_YEAR))}
            >
              contínuo · {formatNumber(CONTINUOUS_HOURS_YEAR)} h
            </Button>
          </div>
        </div>
      </Panel>

      <Panel title={`Candidatos que atendem ao alvo de ${TARGET_TOKENS_PER_SEC} tok/s por requisição`}>
        <DataTable columns={columns} rows={emulable} rowKey={(row) => row.id} />
      </Panel>

      <div className="grid gap-5 lg:grid-cols-3">
        <Panel>
          <Stat
            label={`custo equivalente em nuvem, ${formatNumber(hoursYear)} h/ano`}
            value={Math.round(cloudYear)}
            prefix="US$"
            caption={`a US$ ${formatNumber(CLOUD_USD_HOUR, 2)}/h da instância emulada`}
          />
        </Panel>
        <Panel title="Por que o retorno é rápido" className="lg:col-span-2">
          <p className="text-sm text-kumo-subtle">
            A diferença entre operar na nuvem e operar em casa não é marginal: é de uma ordem de grandeza. O custo
            recorrente próprio é essencialmente energia e refrigeração, enquanto o de nuvem é o preço por hora
            integral. Por isso o parâmetro que mais move o resultado é o regime de uso — e não a tarifa.
          </p>
        </Panel>
      </div>

      {/* O caso não emulável é o mais difícil de comunicar bem, e por isso o que
          mais se beneficia de acordo prévio com o time. */}
      {notEmulable.map((candidate) => (
        <Panel
          key={candidate.id}
          title={
            <span className="flex flex-wrap items-center gap-2">
              {candidate.label}
              <Badge variant="error">fora do ranking — não emulável</Badge>
            </span>
          }
          bodyClassName="flex flex-col gap-5"
        >
          <Notice tone="error" title="Não há instância de bancada equivalente" description={candidate.notEmulableReason} />
          <div className="grid gap-6 md:grid-cols-3">
            <Stat label="custo inicial estimado" value={candidate.capexUsd} prefix="US$" />
            <div className="flex flex-col gap-1 md:col-span-2">
              <span className="text-xs font-medium uppercase tracking-wide text-kumo-subtle">desempenho previsto</span>
              <ProvenanceValue value={candidate.predictedTokensPerSec} suffix="tok/s" size="large" />
            </div>
          </div>
        </Panel>
      ))}
    </Page>
  );
}
